/**
 * Product Card Inventory Status System
 * Fetches "Available" inventory quantity and updates product card status badges
 */

class ProductCardInventory {
  constructor() {
    this.statusElements = new Map();
    this.inventoryCache = new Map();
    this.init();
  }

  init() {
    // Find all product card status elements
    this.findStatusElements();
    
    // Set up observer for dynamically added product cards first
    this.setupObserver();
    
    // Fetch inventory for all products (with small delay to ensure DOM is ready)
    setTimeout(() => {
      this.updateAllStatuses();
    }, 100);
  }

  findStatusElements() {
    const elements = document.querySelectorAll('.product-card__status[data-variant-id]');
    console.log(`Found ${elements.length} product card status elements`);
    elements.forEach(element => {
      const variantId = element.dataset.variantId;
      const productId = element.dataset.productId;
      if (variantId && productId) {
        this.statusElements.set(variantId, {
          element: element,
          productId: productId,
          variantId: variantId
        });
      } else {
        console.warn('Status element missing variant-id or product-id:', element);
      }
    });
    console.log(`Registered ${this.statusElements.size} status elements for update`);
  }

  setupObserver() {
    // Watch for new product cards added to the page
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          const hasNewCards = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1) {
              return node.querySelector?.('.product-card__status[data-variant-id]') || 
                     node.classList?.contains('product-card__status');
            }
            return false;
          });
          
          if (hasNewCards) {
            setTimeout(() => {
              this.findStatusElements();
              this.updateAllStatuses();
            }, 100);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async fetchProductInventory(productId) {
    // Check cache first
    if (this.inventoryCache.has(productId)) {
      return this.inventoryCache.get(productId);
    }

    try {
      // Get product handle from data attribute (we added this to the status element)
      const statusData = Array.from(this.statusElements.values())
        .find(data => data.productId === productId);
      
      if (!statusData || !statusData.element) return null;

      const productHandle = statusData.element.dataset.productHandle;
      if (!productHandle) {
        // Fallback: try to get from product card link
        const productCard = statusData.element.closest('product-card');
        const productLink = productCard?.querySelector('.product-card__link[href*="/products/"]');
        if (productLink) {
          const productUrl = productLink.getAttribute('href');
          const handle = productUrl.match(/\/products\/([^?#]+)/)?.[1];
          if (handle) {
            // Store handle in data attribute for next time
            statusData.element.dataset.productHandle = handle;
            return this.fetchProductInventory(productId); // Retry with handle
          }
        }
        return null;
      }

      // Fetch product JSON data from Shopify
      // This endpoint returns product data including variant inventory
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        console.error(`Failed to fetch product ${productHandle}: ${response.status} ${response.statusText}`);
        return null;
      }

      const product = await response.json();
      
      if (!product || !product.variants) {
        console.warn(`Product ${productHandle} has no variants`);
        return null;
      }
      
      // Store in cache (cache for 30 seconds to avoid too many requests)
      this.inventoryCache.set(productId, product);
      setTimeout(() => {
        this.inventoryCache.delete(productId);
      }, 30000);
      
      return product;
    } catch (error) {
      console.error(`Error fetching inventory for product ${productId}:`, error);
      return null;
    }
  }

  async fetchAvailableInventory(variantId, productId) {
    const product = await this.fetchProductInventory(productId);
    if (!product || !product.variants) {
      console.warn(`No product data for productId ${productId}`);
      return null;
    }

    const variant = product.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) {
      console.warn(`Variant ${variantId} not found in product ${productId}`);
      return null;
    }

    const onHand = variant.inventory_quantity || 0;
    
    // Try to get "Available" quantity from metafields first
    // If you have a Shopify app that tracks "Available" inventory, it should set this metafield
    let availableQuantity = null;
    
    // Check for available inventory metafield (common metafield names)
    if (variant.metafields) {
      // Try different possible metafield paths
      availableQuantity = variant.metafields.custom?.available_inventory || 
                         variant.metafields.custom?.available_quantity ||
                         variant.metafields.inventory?.available ||
                         null;
    }
    
    // If metafield not found, use onHand as the available quantity
    // Note: In most cases, when Committed = 0 and Unavailable = 0, Available = On hand
    // We use onHand instead of checking variant.available (boolean) because:
    // - variant.available can be false even when there's inventory (due to other restrictions)
    // - We want to show status based on actual quantity, not just availability flag
    const available = availableQuantity !== null ? availableQuantity : onHand;
    
    console.log(`[Inventory] Variant ${variantId}: onHand=${onHand}, available=${available}, hasMetafield=${availableQuantity !== null}, variant.available=${variant.available}`);
    
    return {
      available: available,
      onHand: onHand,
      availableForSale: variant.available,
      inventoryManagement: variant.inventory_management,
      isTrueAvailable: availableQuantity !== null // Flag to indicate if this is true "Available" or calculated
    };
  }

  calculateStatus(inventoryData, isMadeToOrder) {
    if (!inventoryData) {
      return {
        status: 'In Transit',
        class: 'product-card__status--in-transit'
      };
    }

    const { available, inventoryManagement } = inventoryData;

    // If inventory tracking is not enabled, use fallback logic
    if (inventoryManagement !== 'shopify') {
      if (isMadeToOrder) {
        return {
          status: 'Made to Order',
          class: 'product-card__status--in-stock'
        };
      }
      return {
        status: 'In Transit',
        class: 'product-card__status--in-transit'
      };
    }

    // Simple logic: Use "Available" quantity directly
    // Available > 0 → "In Stock"
    // Available <= 0 → "In Transit"
    console.log(`[Status Calc] available=${available}, isMadeToOrder=${isMadeToOrder}, inventoryManagement=${inventoryManagement}`);
    
    if (available > 0) {
      console.log(`[Status Calc] Returning: In Stock`);
      return {
        status: 'In Stock',
        class: 'product-card__status--in-stock'
      };
    } else if (isMadeToOrder) {
      console.log(`[Status Calc] Returning: Made to Order`);
      return {
        status: 'Made to Order',
        class: 'product-card__status--in-stock'
      };
    } else {
      console.log(`[Status Calc] Returning: In Transit (available=${available})`);
      return {
        status: 'In Transit',
        class: 'product-card__status--in-transit'
      };
    }
  }

  async updateStatusForVariant(variantId, productId) {
    const statusData = this.statusElements.get(variantId);
    if (!statusData) return;

    const { element } = statusData;
    
    // Check if product is in made-to-order collection from data attribute
    const isMadeToOrder = element.dataset.isMadeToOrder === 'true';

    // Add loading state
    element.classList.add('updating');

    try {
      // Fetch available inventory
      const inventoryData = await this.fetchAvailableInventory(variantId, productId);
      
      if (!inventoryData) {
        console.warn(`No inventory data found for variant ${variantId}`);
        element.classList.remove('updating');
        return;
      }
      
      // Calculate status based on Available quantity
      const status = this.calculateStatus(inventoryData, isMadeToOrder);
      
      console.log(`[Update] Variant ${variantId}: Setting status to "${status.status}" (available=${inventoryData.available}, onHand=${inventoryData.onHand}, class=${status.class})`);
      
      // Update element
      const oldText = element.textContent.trim();
      const oldClass = element.className;
      element.className = `product-card__status ${status.class}`;
      element.textContent = status.status;
      
      // Store available inventory in data attribute for debugging
      element.dataset.availableInventory = inventoryData.available || 0;
      element.dataset.onHandInventory = inventoryData.onHand || 0;
      
      // Verify the update worked
      const newText = element.textContent.trim();
      const newClass = element.className;
      if (newText !== status.status) {
        console.error(`[Update] Text update failed! Expected "${status.status}", got "${newText}"`);
      } else if (newClass !== `product-card__status ${status.class}`) {
        console.error(`[Update] Class update failed! Expected "product-card__status ${status.class}", got "${newClass}"`);
      } else {
        console.log(`[Update] ✓ Successfully updated variant ${variantId}: "${oldText}" → "${status.status}"`);
      }
      
    } catch (error) {
      console.error(`Error updating status for variant ${variantId}:`, error);
    } finally {
      element.classList.remove('updating');
    }
  }

  async updateAllStatuses() {
    if (this.statusElements.size === 0) {
      console.warn('No status elements found to update');
      return;
    }
    
    console.log(`Updating ${this.statusElements.size} product card statuses...`);
    
    // Update all statuses in parallel for faster results
    const updatePromises = [];
    
    this.statusElements.forEach((statusData, variantId) => {
      updatePromises.push(
        this.updateStatusForVariant(variantId, statusData.productId).catch(error => {
          console.error(`Error updating status for variant ${variantId}:`, error);
        })
      );
    });

    // Execute all updates in parallel
    const results = await Promise.allSettled(updatePromises);
    const successful = results.filter(r => r.status === 'fulfilled').length;
    console.log(`Updated ${successful}/${this.statusElements.size} product card statuses`);
  }

  // Public method to refresh a specific product
  async refreshProduct(productId) {
    this.inventoryCache.delete(productId);
    const variants = Array.from(this.statusElements.values())
      .filter(data => data.productId === productId)
      .map(data => data.variantId);
    
    for (const variantId of variants) {
      await this.updateStatusForVariant(variantId, productId);
    }
  }
}

// Initialize when DOM is ready
let productCardInventoryInstance = null;

function initProductCardInventory() {
  if (productCardInventoryInstance) {
    productCardInventoryInstance.findStatusElements();
    productCardInventoryInstance.updateAllStatuses();
  } else {
    productCardInventoryInstance = new ProductCardInventory();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('Initializing ProductCardInventory on DOMContentLoaded');
    initProductCardInventory();
  });
} else {
  // DOM already loaded
  console.log('Initializing ProductCardInventory immediately (DOM already loaded)');
  initProductCardInventory();
}

// Re-initialize on section loads (for Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  console.log('Shopify section loaded, re-initializing ProductCardInventory');
  setTimeout(() => {
    initProductCardInventory();
  }, 100);
});

// Export for use in other scripts
window.ProductCardInventory = ProductCardInventory;
window.initProductCardInventory = initProductCardInventory;

